import React from 'react';
import { Calendar, Clock, Video, MapPin, Phone, Mail, CheckCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BookingPage = () => {
  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-20 bg-[#2C5530]" data-testid="booking-hero">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-white/70 text-sm uppercase tracking-[0.2em] mb-4">Book a Session</p>
            <h1 className="font-['Playfair_Display'] text-4xl md:text-6xl text-white font-medium mb-6">
              Schedule Your Consultation
            </h1>
            <p className="text-white/80 text-xl leading-relaxed">
              Book a free 15-minute discovery call or schedule your first coaching session.
            </p>
          </div>
        </div>
      </section>

      {/* Booking Content */}
      <section className="py-24 md:py-32" data-testid="booking-content">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Session Types */}
            <div>
              <h2 className="font-['Playfair_Display'] text-3xl text-[#1A1A1A] mb-8">
                Session Options
              </h2>

              <div className="space-y-6">
                {/* Discovery Call */}
                <div className="bg-white border border-[#E7E5E4] p-6 hover:border-[#2C5530]/30 transition-colors">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#2C5530]/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-[#2C5530]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1A1A1A] text-lg mb-2">Free Discovery Call</h3>
                      <p className="text-[#57534E] mb-3">
                        A 15-minute call to discuss your goals and see if we're a good fit.
                      </p>
                      <div className="flex items-center gap-4 text-sm text-[#737373]">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          15 minutes
                        </span>
                        <span className="text-[#2C5530] font-semibold">Free</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 1-on-1 In Person */}
                <div className="bg-white border border-[#E7E5E4] p-6 hover:border-[#2C5530]/30 transition-colors">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#BC562B]/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-[#BC562B]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1A1A1A] text-lg mb-2">In-Person Coaching</h3>
                      <p className="text-[#57534E] mb-3">
                        1-on-1 coaching session. Contact for availability and pricing.
                      </p>
                      <div className="flex items-center gap-4 text-sm text-[#737373]">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          60 minutes
                        </span>
                        <span className="text-[#2C5530] font-semibold">Contact for pricing</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Online Session */}
                <div className="bg-white border border-[#E7E5E4] p-6 hover:border-[#2C5530]/30 transition-colors">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#2C5530]/10 flex items-center justify-center flex-shrink-0">
                      <Video className="w-6 h-6 text-[#2C5530]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1A1A1A] text-lg mb-2">Online Coaching</h3>
                      <p className="text-[#57534E] mb-3">
                        Virtual coaching session via Zoom from anywhere in the world.
                      </p>
                      <div className="flex items-center gap-4 text-sm text-[#737373]">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          60 minutes
                        </span>
                        <span className="text-[#2C5530] font-semibold">Contact for pricing</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* What to Expect */}
              <div className="bg-[#F2F0E9] p-8 mt-8">
                <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-4">
                  What to Expect
                </h3>
                <ul className="space-y-3 text-[#57534E]">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#2C5530] mt-0.5 flex-shrink-0" />
                    <span>Personalized approach based on your specific goals</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#2C5530] mt-0.5 flex-shrink-0" />
                    <span>Practical techniques you can use immediately</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#2C5530] mt-0.5 flex-shrink-0" />
                    <span>Follow-up resources and exercises</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#2C5530] mt-0.5 flex-shrink-0" />
                    <span>Access to client portal for progress tracking</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Calendly Embed */}
            <div>
              <h2 className="font-['Playfair_Display'] text-3xl text-[#1A1A1A] mb-8">
                Book Your Session
              </h2>
              
              {/* Calendly Embed Placeholder */}
              <div className="bg-white border border-[#E7E5E4] p-8" data-testid="calendly-embed">
                <div className="text-center py-12">
                  <Calendar className="w-16 h-16 text-[#2C5530] mx-auto mb-6" />
                  <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-4">
                    Online Booking Coming Soon
                  </h3>
                  <p className="text-[#57534E] mb-6">
                    Our online booking system is being set up. In the meantime, please contact me directly to schedule your session.
                  </p>
                  
                  <div className="space-y-4">
                    <a
                      href="mailto:scott@quietmindgolf.co.uk"
                      className="flex items-center justify-center gap-3 w-full bg-[#2C5530] text-white px-6 py-4 font-semibold hover:bg-[#1E3A21] transition-colors"
                      data-testid="email-booking-btn"
                    >
                      <Mail className="w-5 h-5" />
                      Email to Book: scott@quietmindgolf.co.uk
                    </a>
                    <a
                      href="tel:+447455558064"
                      className="flex items-center justify-center gap-3 w-full border-2 border-[#2C5530] text-[#2C5530] px-6 py-4 font-semibold hover:bg-[#2C5530] hover:text-white transition-colors"
                      data-testid="phone-booking-btn"
                    >
                      <Phone className="w-5 h-5" />
                      Call: +44 7455 558064
                    </a>
                  </div>
                </div>

                {/* When Calendly is ready, uncomment this:
                <iframe
                  src="https://calendly.com/quietmindgolf"
                  width="100%"
                  height="700"
                  frameBorder="0"
                  title="Schedule a session"
                ></iframe>
                */}
              </div>

              <p className="text-sm text-[#737373] mt-4 text-center">
                All times shown in your local timezone
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BookingPage;
